import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';
import { useLogout } from '../../hooks/useLogout';
import { useNotification } from '../../hooks/useNotification';
import {
    FaChartLine,
    FaCalendarAlt,
    FaCog,
    FaSignOutAlt,
    FaBars,
    FaTimes,
    FaShieldAlt,
    FaUser,
    FaArrowRight,
    FaChevronDown,
    FaChevronUp,
    FaUserPlus
} from 'react-icons/fa';
import styles from './styles/AdminNav.module.css';

export default function AdminNav() {
    const { user } = useContext(AuthContext);
    const { isAdmin, loading } = useUserRole();
    const { handleLogout } = useLogout();
    const { showNotification } = useNotification();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [expandedMenu, setExpandedMenu] = useState(null);
    const [logoutLoading, setLogoutLoading] = useState(false);


    if (!isAdmin || loading) {
        return null;
    }


    const onLogout = async () => {
        setLogoutLoading(true);
        try {
            await handleLogout();
            showNotification('Déconnexion réussie', 'success');
            setMobileMenuOpen(false);
        } catch (error) {
            showNotification(`Erreur: ${error.message}`, 'error');
            setLogoutLoading(false);
        }
    };

    const toggleMenu = (menuName) => {
        setExpandedMenu(expandedMenu === menuName ? null : menuName);
    };

    const handleNavClick = () => {
        setMobileMenuOpen(false);
        setExpandedMenu(null);
    };

    const isActive = (path) => location.pathname === path;

    // Menu items - Récupérés de l'ancien menu
    const menuItems = [
        {
            id: 'dashboard',
            label: 'Tableau de Bord',
            icon: FaChartLine,
            path: '/admin/dashboard',
            badge: null
        },
        {
            id: 'reservations',
            label: 'Réservations',
            icon: FaCalendarAlt,
            path: '/admin/reservations',
            badge: null
        },
        {
            id: 'create-admin',
            label: 'Créer Admin',
            icon: FaUserPlus,
            path: '/admin/create-admin',
            badge: null
        },
        {
            id: 'settings',
            label: 'Paramètres',
            icon: FaCog,
            submenu: [
                { label: 'Horaires du salon', path: '/admin/settings/hours' },
                { label: 'Utilisateurs', path: '/admin/settings/users' },
                { label: 'Paramètres généraux', path: '/admin/settings/general' }
            ]
        }
    ];

    return (
        <>
            {/* Admin Navigation Bar */}
            <nav className={styles.adminNav}>
                <div className={styles.container}>
                    {/* Left Section - Logo & Brand */}
                    <div className={styles.leftSection}>
                        <Link to="/admin/dashboard" className={styles.adminBrand} onClick={handleNavClick}>
                            <FaShieldAlt className={styles.brandIcon} />
                            <span className={styles.brandText}>Admin Panel</span>
                        </Link>
                    </div>

                    {/* Desktop Menu */}
                    <div className={`${styles.desktopMenu} ${mobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
                        {menuItems.map((item) => (
                            <div key={item.id} className={styles.menuItem}>
                                {item.submenu ? (
                                    // Menu with submenu
                                    <button
                                        className={`${styles.menuButton} ${expandedMenu === item.id ? styles.expanded : ''
                                            }`}
                                        onClick={() => toggleMenu(item.id)}
                                    >
                                        <span className={styles.menuIcon}>
                                            {React.createElement(item.icon)}
                                        </span>
                                        <span className={styles.menuLabel}>{item.label}</span>
                                        <span className={styles.chevron}>
                                            {expandedMenu === item.id ? (
                                                <FaChevronUp />
                                            ) : (
                                                <FaChevronDown />
                                            )}
                                        </span>
                                    </button>
                                ) : (
                                    // Direct link
                                    <Link
                                        to={item.path}
                                        className={`${styles.menuButton} ${isActive(item.path) ? styles.active : ''
                                            }`}
                                        onClick={handleNavClick}
                                    >
                                        <span className={styles.menuIcon}>
                                            {React.createElement(item.icon)}
                                        </span>
                                        <span className={styles.menuLabel}>{item.label}</span>
                                        {item.badge && (
                                            <span className={styles.badge}>{item.badge}</span>
                                        )}
                                    </Link>
                                )}

                                {/* Submenu */}
                                {item.submenu && expandedMenu === item.id && (
                                    <div className={styles.submenu}>
                                        {item.submenu.map((subitem, idx) => (
                                            <Link
                                                key={idx}
                                                to={subitem.path}
                                                className={`${styles.submenuItem} ${isActive(subitem.path) ? styles.active : ''
                                                    }`}
                                                onClick={handleNavClick}
                                            >
                                                <FaArrowRight className={styles.submenuIcon} />
                                                <span>{subitem.label}</span>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Right Section - User & Logout */}
                    <div className={styles.rightSection}>
                        <div className={styles.userInfo}>
                            <div className={styles.userAvatar}>
                                <FaUser />
                            </div>
                            <div className={styles.userDetails}>
                                <div className={styles.userName}>Admin</div>
                                <div className={styles.userEmail}>{user?.email}</div>
                            </div>
                        </div>

                        <button
                            className={styles.logoutBtn}
                            onClick={onLogout}
                            disabled={logoutLoading}
                            title="Déconnexion"
                        >
                            <FaSignOutAlt />
                        </button>
                    </div>

                    {/* Mobile Hamburger */}
                    <button
                        className={styles.hamburger}
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <FaTimes /> : <FaBars />}
                    </button>
                </div>

                {/* Mobile Menu Overlay */}
                {mobileMenuOpen && (
                    <div
                        className={styles.mobileOverlay}
                        onClick={() => setMobileMenuOpen(false)}
                    />
                )}
            </nav>

            {/* Spacer */}
            <div className={styles.spacer} />
        </>
    );
}
